import React, { useState, useCallback, useMemo } from "react";
import Slider from "@mui/material/Slider";
import Box from "@mui/material/Box";
import "../style/FilterSidebar.css";

function Chevron(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CheckIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function FilterSection({ title, activeCount = 0, defaultOpen = false, bodyClassName = "fs-section-body", children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="fs-section">
      <button
        type="button"
        className="fs-section-header"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="fs-section-header-left">
          <span className="fs-section-label">{title}</span>
          {activeCount > 0 && <span className="fs-section-active">{activeCount}</span>}
        </span>
        <Chevron className="fs-chevron" />
      </button>
      <div className={`fs-section-collapse ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <div className="fs-section-collapse-inner">
          <div className={bodyClassName}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ name, checked, onChange, label, typeIcon }) {
  return (
    <label className={`fs-chip ${typeIcon ? "fs-chip-type" : ""} ${checked ? "is-checked" : ""}`}>
      <input
        type="checkbox"
        name={name}
        checked={!!checked}
        onChange={onChange}
      />
      {typeIcon && <img className="fs-chip-type-img" src={typeIcon} alt="" />}
      <CheckIcon className="fs-chip-check" />
      <span className="fs-chip-text">{label}</span>
    </label>
  );
}

function FilterBox({
  checkboxes,
  handleCheckboxChange,
  sliderValue,
  handleSliderChange1,
  handleSliderChange2,
  handleSliderChange3,
  isVisible,
  isModelMenuVisible,
  marcas,
  modelos,
  handleResetFilters,
  isAnyFilterActive,
}) {
  const currentYear = new Date().getFullYear();
  const yearMin = 2009;
  const yearMax = currentYear + 1;

  const yearMarks = useMemo(
    () => [
      { value: yearMin, label: String(yearMin) },
      { value: yearMax, label: String(yearMax) },
    ],
    [yearMin, yearMax]
  );

  const priceMarks = useMemo(
    () => [
      { value: 0.0, label: "L. 0" },
      { value: 500000.0, label: "L. 500k" },
    ],
    []
  );

  const motorMarks = useMemo(
    () => [
      { value: 1.0, label: "1.0L" },
      { value: 7.0, label: "7.0L" },
    ],
    []
  );

  const countChecked = useCallback(
    (...keys) => keys.reduce((n, k) => n + (checkboxes?.[k] ? 1 : 0), 0),
    [checkboxes]
  );

  const marcaCount = useMemo(
    () => marcas.reduce((n, m) => n + (checkboxes?.[m] ? 1 : 0), 0),
    [marcas, checkboxes]
  );

  const modeloCount = useMemo(
    () => modelos.reduce((n, m) => n + (checkboxes?.[m] ? 1 : 0), 0),
    [modelos, checkboxes]
  );

  const conditionCount = countChecked("chocados", "buenEstado", "arreglado");
  const locationCount = countChecked("Usa", "Puerto", "Tegucigalpa", "Transito", "SanLorenzo");
  const tipoCount = countChecked("turismo", "camioneta", "pickup", "camion", "camioncito", "cabezal", "bus");
  const fuelCount = countChecked("gasolina", "diesel");
  const transmissionCount = countChecked("automatica", "manual");
  const tractionCount = countChecked("delantera", "trasera", "ruedas");

  const totalActive =
    conditionCount +
    marcaCount +
    modeloCount +
    locationCount +
    tipoCount +
    fuelCount +
    transmissionCount +
    tractionCount;

  const showResetButton = isAnyFilterActive();

  const [marcaSearch, setMarcaSearch] = useState("");
  const [modeloSearch, setModeloSearch] = useState("");

  const filteredMarcas = useMemo(() => {
    const q = marcaSearch.trim().toLowerCase();
    if (!q) return marcas;
    return marcas.filter((m) => m.toLowerCase().includes(q));
  }, [marcas, marcaSearch]);

  const filteredModelos = useMemo(() => {
    const q = modeloSearch.trim().toLowerCase();
    if (!q) return modelos;
    return modelos.filter((m) => m.toLowerCase().includes(q));
  }, [modelos, modeloSearch]);

  if (!isVisible) return null;

  const tipoChips = [
    { name: "turismo", label: "Turismo", icon: "/images/sedan.png" },
    { name: "camioneta", label: "Camioneta", icon: "/images/camioneta.png" },
    { name: "pickup", label: "Pick up", icon: "/images/pick-up.png" },
    { name: "camion", label: "Camión", icon: "/images/truck.png" },
    { name: "camioncito", label: "Camioncito", icon: "/images/camioncito.png" },
    { name: "cabezal", label: "Cabezal", icon: "/images/cabezal.png" },
    { name: "bus", label: "Bus", icon: "/images/bus.png" },
  ];

  return (
    <div className="fs-sidebar">
      <div className="fs-header">
        <div className="fs-title-wrap">
          <h3 className="fs-title">Filtros</h3>
          {totalActive > 0 && <span className="fs-count">{totalActive}</span>}
        </div>
        {showResetButton && (
          <button type="button" className="fs-reset" onClick={handleResetFilters}>
            Limpiar
          </button>
        )}
      </div>

      <div className="fs-scroll">
        <FilterSection title="Condición" activeCount={conditionCount} defaultOpen>
          <Chip name="chocados" label="Chocados" checked={checkboxes.chocados} onChange={handleCheckboxChange} />
          <Chip name="buenEstado" label="Buen Estado" checked={checkboxes.buenEstado} onChange={handleCheckboxChange} />
          <Chip name="arreglado" label="Arreglado" checked={checkboxes.arreglado} onChange={handleCheckboxChange} />
        </FilterSection>

        <FilterSection title="Marca" activeCount={marcaCount} bodyClassName="fs-section-searchable">
          <div className="fs-search">
            <input
              type="text"
              className="fs-search-input"
              placeholder="Buscar marca"
              value={marcaSearch}
              onChange={(e) => setMarcaSearch(e.target.value)}
            />
          </div>
          <div className="fs-chip-grid fs-chip-grid-scroll">
            {filteredMarcas.length === 0 ? (
              <p className="fs-empty">Sin coincidencias</p>
            ) : (
              filteredMarcas.map((marca) => (
                <Chip
                  key={marca}
                  name={marca}
                  label={marca}
                  checked={checkboxes[marca]}
                  onChange={handleCheckboxChange}
                />
              ))
            )}
          </div>
        </FilterSection>

        {isModelMenuVisible && (
          <FilterSection
            title="Modelos"
            activeCount={modeloCount}
            defaultOpen
            bodyClassName="fs-section-searchable"
          >
            <div className="fs-search">
              <input
                type="text"
                className="fs-search-input"
                placeholder="Buscar modelo"
                value={modeloSearch}
                onChange={(e) => setModeloSearch(e.target.value)}
              />
            </div>
            <div className="fs-chip-grid fs-chip-grid-scroll">
              {filteredModelos.length === 0 ? (
                <p className="fs-empty">Sin coincidencias</p>
              ) : (
                filteredModelos.map((modelo) => (
                  <Chip
                    key={modelo}
                    name={modelo}
                    label={modelo}
                    checked={checkboxes[modelo] || false}
                    onChange={handleCheckboxChange}
                  />
                ))
              )}
            </div>
          </FilterSection>
        )}

        <FilterSection title="Ubicación" activeCount={locationCount}>
          <Chip name="Usa" label="USA" checked={checkboxes.Usa} onChange={handleCheckboxChange} />
          <Chip name="Puerto" label="Puerto Cortez" checked={checkboxes.Puerto} onChange={handleCheckboxChange} />
          <Chip name="Tegucigalpa" label="Tegucigalpa" checked={checkboxes.Tegucigalpa} onChange={handleCheckboxChange} />
          <Chip name="Transito" label="Transito" checked={checkboxes.Transito} onChange={handleCheckboxChange} />
          <Chip name="SanLorenzo" label="San Lorenzo" checked={checkboxes.SanLorenzo} onChange={handleCheckboxChange} />
        </FilterSection>

        <FilterSection title="Año" bodyClassName="fs-range">
          <Box sx={{ width: "100%", px: 1 }}>
            <Slider
              aria-label="Año"
              defaultValue={sliderValue}
              min={yearMin}
              step={1}
              max={yearMax}
              marks={yearMarks}
              valueLabelDisplay="auto"
              onChange={handleSliderChange1}
            />
          </Box>
        </FilterSection>

        <FilterSection title="Tipo de Auto" activeCount={tipoCount}>
          {tipoChips.map((t) => (
            <Chip
              key={t.name}
              name={t.name}
              label={t.label}
              typeIcon={t.icon}
              checked={checkboxes[t.name]}
              onChange={handleCheckboxChange}
            />
          ))}
        </FilterSection>

        <FilterSection title="Combustible" activeCount={fuelCount}>
          <Chip name="gasolina" label="Gasolina" checked={checkboxes.gasolina} onChange={handleCheckboxChange} />
          <Chip name="diesel" label="Diesel" checked={checkboxes.diesel} onChange={handleCheckboxChange} />
        </FilterSection>

        <FilterSection title="Precio" bodyClassName="fs-range">
          <Box sx={{ width: "100%", px: 1 }}>
            <Slider
              aria-label="Precio"
              defaultValue={sliderValue}
              min={0.0}
              step={100}
              max={500000.0}
              marks={priceMarks}
              valueLabelDisplay="auto"
              onChange={handleSliderChange2}
            />
          </Box>
        </FilterSection>

        <FilterSection title="Motor" bodyClassName="fs-range">
          <Box sx={{ width: "100%", px: 1 }}>
            <Slider
              aria-label="Motor"
              defaultValue={sliderValue}
              min={1.0}
              step={0.1}
              max={7.0}
              marks={motorMarks}
              valueLabelDisplay="auto"
              onChange={handleSliderChange3}
            />
          </Box>
        </FilterSection>

        <FilterSection title="Transmisión" activeCount={transmissionCount}>
          <Chip name="automatica" label="Automática" checked={checkboxes.automatica} onChange={handleCheckboxChange} />
          <Chip name="manual" label="Manual" checked={checkboxes.manual} onChange={handleCheckboxChange} />
        </FilterSection>

        <FilterSection title="Tracción" activeCount={tractionCount}>
          <Chip name="delantera" label="Delantera" checked={checkboxes.delantera} onChange={handleCheckboxChange} />
          <Chip name="trasera" label="Trasera" checked={checkboxes.trasera} onChange={handleCheckboxChange} />
          <Chip name="ruedas" label="4 ruedas" checked={checkboxes.ruedas} onChange={handleCheckboxChange} />
        </FilterSection>
      </div>
    </div>
  );
}

export default FilterBox;
